import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, Phone, MapPin, CreditCard } from "lucide-react";
import { BRAND } from "../../data/content";
import { FOOTER } from "../../constants/testIds";
import { IAWordmark, PHWordmark } from "./Wordmarks";
import ArchDivider from "./ArchDivider";

export default function Footer() {
  return (
    <footer
      data-testid={FOOTER.root}
      className="relative bg-[#1A100D] text-[#FDFBF6] pt-24 md:pt-32 overflow-hidden"
    >
      <div className="absolute inset-0 dot-grid opacity-20 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <div className="flex justify-center mb-16 text-[#C9A961]">
          <ArchDivider />
        </div>

        {/* Dual logos — IA on left, PH on right */}
        <div className="pb-14 md:pb-20 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div className="inline-flex items-center bg-[#FDFBF6] px-4 py-3 rounded self-start">
            <IAWordmark height={52} />
          </div>
          <div className="hidden md:block h-px flex-1 mx-8 bg-[#C9A961]/25" />
          <div className="inline-flex items-center bg-[#FDFBF6] px-4 py-3 rounded self-start md:self-auto">
            <PHWordmark height={48} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 pb-16 md:pb-24 border-t border-white/10 pt-16 md:pt-20">
          <div className="md:col-span-5">
            <p className="font-serif text-2xl md:text-3xl leading-snug text-[#FDFBF6]/90 max-w-md">
              Where Indian cuisine is <em className="italic text-[#C9A961]">curated</em>, not just cooked.
            </p>
            <p className="mt-6 text-sm text-[#FDFBF6]/80 leading-relaxed max-w-md">
              Third-generation hospitality. Luxury event catering under Indian Alchemy™ · Corporate catering powerhouse under Platinum Hospitality — 25 years, 4,500 meals daily, one family kitchen.
            </p>
          </div>

          <div className="md:col-span-3 space-y-4">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">Reach us</div>
            <a
              href={`mailto:${BRAND.email}`}
              data-testid={FOOTER.emailLink}
              className="flex items-center gap-3 text-sm text-[#FDFBF6]/85 hover:text-[#C9A961]"
            >
              <Mail size={14} /> {BRAND.email}
            </a>
            <a
              href={`tel:${BRAND.phone.replace(/\s+/g, "")}`}
              data-testid={FOOTER.phoneLink}
              className="flex items-center gap-3 text-sm text-[#FDFBF6]/85 hover:text-[#C9A961]"
            >
              <Phone size={14} /> {BRAND.phone}
            </a>
            <a
              href={`tel:${BRAND.phoneAtul.replace(/\s+/g, "")}`}
              className="flex items-center gap-3 text-sm text-[#FDFBF6]/85 hover:text-[#C9A961]"
            >
              <Phone size={14} /> {BRAND.phoneAtul} · Atul
            </a>
            <div className="flex items-start gap-3 text-sm text-[#FDFBF6]/85">
              <MapPin size={14} className="mt-1 flex-shrink-0" /> {BRAND.address}
            </div>
            <div className="flex items-center gap-4 pt-2">
              <a
                href={BRAND.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={FOOTER.instagram}
                className="text-[#FDFBF6]/85 hover:text-[#C9A961]"
              >
                <Instagram size={16} />
              </a>
              <a
                href={BRAND.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={FOOTER.facebook}
                className="text-[#FDFBF6]/85 hover:text-[#C9A961]"
              >
                <Facebook size={16} />
              </a>
            </div>
          </div>

          <div className="md:col-span-4 grid grid-cols-2 gap-8">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">Explore</div>
              <ul className="mt-4 space-y-2 text-sm text-[#FDFBF6]/85">
                <li><Link className="hover:text-[#C9A961]" to="/">Home</Link></li>
                <li><Link className="hover:text-[#C9A961]" to="/about">About / Founders</Link></li>
                <li><Link className="hover:text-[#C9A961]" to="/indian-alchemy">Indian Alchemy</Link></li>
                <li><Link className="hover:text-[#C9A961]" to="/platinum-hospitality">Platinum Hospitality</Link></li>
                <li><Link className="hover:text-[#C9A961]" to="/portfolio">Portfolio</Link></li>
              </ul>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">Action</div>
              <ul className="mt-4 space-y-2 text-sm text-[#FDFBF6]/85">
                <li><Link className="hover:text-[#C9A961]" to="/book">Book a Consultation</Link></li>
                <li><Link className="hover:text-[#C9A961]" to="/contact">Contact</Link></li>
                <li>
                  <Link
                    to="/deposit"
                    data-testid={FOOTER.depositLink}
                    className="inline-flex items-center gap-2 hover:text-[#C9A961]"
                  >
                    <CreditCard size={12} /> Client Payments
                  </Link>
                </li>
              </ul>
              <div className="mt-6 text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">Service Area</div>
              <div className="mt-3 text-sm text-[#FDFBF6]/85">{BRAND.serviceArea.join(" · ")}</div>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 py-8 text-xs text-[#FDFBF6]/75">
          <div>© {new Date().getFullYear()} Indian Alchemy™ · {BRAND.parent}. All rights reserved.</div>
          <div className="flex items-center gap-6 flex-wrap">
            <span>Est. 2001 · Delhi NCR</span>
            <span className="text-[#C9A961]">Third-generation family business</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
